/**
 * Google Analytics 4 Event Tracking Utilities
 *
 * This module provides helper functions for tracking custom events in GA4.
 * All functions safely check if gtag is available before attempting to track.
 */

/** A value GA4 accepts as an event parameter. */
export type GtagParamValue = string | number | boolean | null | undefined;

/** Event parameters passed alongside a GA4 event name. */
export type GtagParams = Record<string, GtagParamValue>;

/**
 * The gtag call signatures this app actually uses. GA4's own typings are
 * broader, but narrowing to these three keeps call sites checked instead of
 * silently accepting anything.
 */
type GtagFn = {
  (command: 'event', eventName: string, params?: GtagParams): void;
  (command: 'config', targetId: string, params?: GtagParams): void;
  (command: 'js', config: Date): void;
};

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
  }
}

/**
 * Generic event tracking function
 * @param eventName - The name of the event to track
 * @param parameters - Optional event parameters
 */
export function trackEvent(eventName: string, parameters?: GtagParams) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
}

/**
 * Track contact form submissions
 * @param formName - Optional identifier for which form was submitted
 */
export function trackContactSubmit(formName: string = 'main_contact') {
  trackEvent('contact_submit', {
    form_name: formName,
  });
}

/**
 * Track newsletter signup submissions
 * @param location - Where the signup occurred (e.g., 'footer', 'blog_post')
 */
export function trackNewsletterSignup(location: string = 'unknown') {
  trackEvent('newsletter_signup', {
    location,
  });
}

/**
 * Track blog post reading engagement
 * @param title - The title of the blog post
 * @param readingTime - Estimated reading time in minutes
 */
export function trackBlogRead(title: string, readingTime?: number) {
  trackEvent('blog_read', {
    article_title: title,
    reading_time: readingTime,
  });
}

/**
 * Track CTA button clicks
 * @param ctaName - Identifier for the CTA button
 * @param destination - Where the CTA leads
 */
export function trackCTAClick(ctaName: string, destination?: string) {
  trackEvent('cta_click', {
    cta_name: ctaName,
    destination,
  });
}

/**
 * Track outbound link clicks
 * @param url - The destination URL
 * @param linkText - The text of the link clicked
 */
export function trackOutboundLink(url: string, linkText?: string) {
  trackEvent('outbound_click', {
    url,
    link_text: linkText,
  });
}
